import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useI18n } from '../i18n';
import { isKnownIsraelStreet, searchIsraelStreets, streetsForCityWire } from '../utils/israelStreets';
import formStyles from './DynamicRecordForm.module.css';
import styles from './IsraelSettlementCitySelect.module.css';

type IsraelSettlementStreetSelectProps = {
  id: string;
  label: string;
  cityWire: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  mandatory?: boolean;
  theme?: 'dark' | 'light';
  rowClassName?: string;
};

export function IsraelSettlementStreetSelect({
  id,
  label,
  cityWire,
  value,
  onChange,
  disabled = false,
  readOnly = false,
  mandatory = false,
  theme = 'dark',
  rowClassName,
}: IsraelSettlementStreetSelectProps) {
  const { t } = useI18n();
  const listId = useId();
  const overrideId = `${id}-override`;
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [manualOverride, setManualOverride] = useState(() => {
    const trimmed = value.trim();
    return Boolean(trimmed && cityWire.trim() && !isKnownIsraelStreet(cityWire, trimmed));
  });

  const cityReady = cityWire.trim().length > 0;
  const streetCount = streetsForCityWire(cityWire).length;
  const pickerEnabled = cityReady && streetCount > 0 && !manualOverride;

  const displayValue = value;
  const inputValue = open && pickerEnabled ? draft : displayValue;

  const options = useMemo(
    () => (pickerEnabled ? searchIsraelStreets(cityWire, open ? draft : value) : []),
    [cityWire, draft, open, pickerEnabled, value],
  );

  const closeMenu = useCallback(() => {
    setOpen(false);
    setDraft('');
    setActiveIndex(0);
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onDocPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target;
      if (!(target instanceof Node) || !wrapRef.current?.contains(target)) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', onDocPointerDown);
    document.addEventListener('touchstart', onDocPointerDown);
    return () => {
      document.removeEventListener('mousedown', onDocPointerDown);
      document.removeEventListener('touchstart', onDocPointerDown);
    };
  }, [closeMenu, open]);

  useEffect(() => {
    const trimmed = value.trim();
    if (!trimmed || !cityWire.trim()) {
      return;
    }
    if (!isKnownIsraelStreet(cityWire, trimmed)) {
      setManualOverride(true);
    }
  }, [cityWire, value]);

  const pickStreet = useCallback(
    (street: string) => {
      onChange(street);
      closeMenu();
    },
    [closeMenu, onChange],
  );

  const onInputFocus = () => {
    if (disabled || readOnly || !pickerEnabled) {
      return;
    }
    setDraft(displayValue);
    setOpen(true);
    setActiveIndex(0);
  };

  const onInputChange = (next: string) => {
    if (disabled || readOnly) {
      return;
    }
    if (manualOverride || !pickerEnabled) {
      onChange(next);
      return;
    }
    setDraft(next);
    setOpen(true);
    setActiveIndex(0);
    if (isKnownIsraelStreet(cityWire, next)) {
      onChange(next);
    }
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!pickerEnabled) {
      return;
    }
    if (!open && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      setDraft(displayValue);
      setOpen(true);
      return;
    }
    if (!open || options.length === 0) {
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, options.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const picked = options[activeIndex];
      if (picked) {
        pickStreet(picked);
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      closeMenu();
    }
  };

  const inputClass = [formStyles.textInput, styles.input].filter(Boolean).join(' ');
  const menuClass = [styles.menu, theme === 'light' ? styles.menuLight : ''].filter(Boolean).join(' ');
  const hintClass = [styles.hint, theme === 'light' ? styles.hintLight : ''].filter(Boolean).join(' ');

  const inputDisabled = disabled || readOnly || !cityReady;
  const placeholder = !cityReady
    ? t('form.picklist.selectCityFirst')
    : manualOverride || streetCount === 0
      ? t('form.picklist.streetManualPlaceholder')
      : t('form.picklist.searchStreet');

  return (
    <div className={[rowClassName ?? formStyles.fieldRow, styles.wrap].join(' ')} ref={wrapRef}>
      <label className={formStyles.inputLabel} htmlFor={id}>
        {label}
        {mandatory ? <span className={formStyles.mandatoryAsterisk} aria-hidden="true"> *</span> : null}
      </label>
      <input
        id={id}
        className={inputClass}
        type="text"
        role={pickerEnabled ? 'combobox' : undefined}
        aria-expanded={pickerEnabled ? open : undefined}
        aria-controls={pickerEnabled && open ? listId : undefined}
        aria-autocomplete={pickerEnabled ? 'list' : undefined}
        autoComplete="off"
        value={inputValue}
        disabled={inputDisabled}
        readOnly={readOnly}
        placeholder={placeholder}
        onFocus={onInputFocus}
        onChange={(event) => onInputChange(event.target.value)}
        onKeyDown={onInputKeyDown}
        aria-required={mandatory || undefined}
      />
      {pickerEnabled && open && options.length > 0 ? (
        <ul id={listId} className={menuClass} role="listbox">
          {options.map((street, index) => {
            const optionClass = [styles.option, index === activeIndex ? styles.optionActive : '']
              .filter(Boolean)
              .join(' ');
            return (
              <li key={`${street}-${index}`} role="presentation">
                <button
                  type="button"
                  className={optionClass}
                  role="option"
                  aria-selected={index === activeIndex}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => pickStreet(street)}
                >
                  {street}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
      <label className={styles.overrideRow} htmlFor={overrideId}>
        <input
          id={overrideId}
          type="checkbox"
          checked={manualOverride}
          disabled={inputDisabled}
          onChange={(event) => {
            const next = event.target.checked;
            setManualOverride(next);
            closeMenu();
          }}
        />
        <span>{t('form.picklist.streetManualOverride')}</span>
      </label>
      <p className={hintClass}>
        {!cityReady
          ? t('form.picklist.streetNeedsCity')
          : streetCount > 0
            ? t('form.picklist.streetHint', { count: String(streetCount) })
            : t('form.picklist.streetNoOfficialList')}
      </p>
    </div>
  );
}
